/* eslint-disable react/no-unescaped-entities */
import Link from "next/link";
import { Layout } from "components";
import { Article, SectionSmall, Section, FlexColumn } from "styles";

const CONTACT_EMAIL = "wishboxappinfo@gmail.com";

const legalStyle = {
	lineHeight: 1.6,
	color: "#222"
};

const headingStyle = {
	marginTop: 28,
	marginBottom: 8
};

const tableStyle = {
	width: "100%",
	borderCollapse: "collapse",
	margin: "16px 0"
};

const cellStyle = {
	border: "1px solid #ccc",
	padding: 8,
	verticalAlign: "top",
	textAlign: "left"
};

export default function Privacy() {
	return (
		<Layout
			title="Privacy Policy"
			description="Privacy policy for Wishbox Finance. On-device data stays local unless you use an account, collaboration, cloud sync, or the AI assistant."
		>
			<Section>
				<Article>
					<SectionSmall>
						<FlexColumn style={legalStyle}>
							<div>
								<strong>PRIVACY NOTICE</strong>
							</div>
							<div>
								<strong>Last updated September 15, 2026</strong>
							</div>
							<p>
								This privacy notice for Kostas Filios ("<strong>Company</strong>," "
								<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"), located in
								Athens, Greece, describes how and why we might collect, store, use, and/or share ("
								<strong>process</strong>") your information when you use our services ("
								<strong>Services</strong>"), including:
							</p>
							<ul>
								<li>the Wishbox Finance mobile application</li>
								<li>
									the website at <a href="https://wishboxapp.com">https://wishboxapp.com</a>
								</li>
								<li>
									the Hermes API at{" "}
									<a href="https://wishboxapp.com/api">https://wishboxapp.com/api</a>
								</li>
								<li>any related sales, support, or account features that link to this notice</li>
							</ul>
							<p>
								<strong>Questions or concerns?</strong> If you do not agree with our policies and
								practices, please do not use our Services. Contact us at{" "}
								<strong>
									<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
								</strong>
								.
							</p>

							<h2 id="toc" style={headingStyle}>
								SUMMARY OF KEY POINTS
							</h2>
							<p>
								<strong>Local-first by default.</strong> You can use Wishbox to track transactions,
								wishboxes, and budgets on your device without creating an account. That on-device
								data is not uploaded unless you choose a cloud feature described below.
							</p>
							<p>
								<strong>What personal information do we process?</strong> It depends on how you use
								the Services. An optional account, Wishbox+, Collaboration, Cloud Sync, push
								notifications, and the AI Assistant each send specific data off the device.{" "}
								<a href="#personalinfo">Learn more</a>.
							</p>
							<p>
								<strong>Do we process sensitive personal information?</strong> We do not collect
								Apple or GDPR special-category data such as health, race, or biometric identifiers.
								If you use Collaboration, Cloud Sync, or the AI Assistant, financial details you
								enter (amounts, categories, descriptions, wishbox titles) may leave the device.
								Optional voice input is processed for speech-to-text and is not used to create a
								voiceprint.
							</p>
							<p>
								<strong>Do we receive information from third parties?</strong> Yes, when you choose
								those features: Apple or Google for sign-in and in-app purchases, Apple or Google
								for push delivery, OpenAI when you use Wishbox Assistant, and Apple iCloud or Google
								Drive when you turn on Cloud Sync.
							</p>
							<p>
								<strong>How do we process your information?</strong> To provide, secure, and
								administer the Services you ask for (accounts, subscriptions, collaboration,
								assistant), and to comply with law. We do not sell personal information and we do
								not use it for advertising. <a href="#infouse">Learn more</a>.
							</p>
							<p>
								<strong>Your rights.</strong> Depending on where you live, you may have rights to
								access, correct, or delete your information. Email{" "}
								<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.{" "}
								<a href="#privacyrights">Learn more</a>.
							</p>

							<h2 id="personalinfo" style={headingStyle}>
								1. WHAT INFORMATION DO WE COLLECT?
							</h2>
							<p>
								<em>
									<strong>In Short:</strong> We collect information you provide, information created
									when you use optional cloud features, and limited technical data needed to run the
									Services.
								</em>
							</p>

							<h3>Information that stays on your device</h3>
							<p>
								Transactions, wishboxes, categories, budgets, backups, and CSV/Excel imports are
								stored in the app database on your device. We do not receive this data unless you
								sign in and use Collaboration, Wishbox Assistant, Cloud Sync, or another feature
								that you explicitly enable. The Apple Watch companion shows data stored on your
								paired phone. If you use Wishbox Assistant from the Watch, the same assistant terms
								and privacy practices apply.
							</p>

							<h3>Account information (optional)</h3>
							<p>If you create an account or sign in with Apple or Google, we may process:</p>
							<ul>
								<li>email address</li>
								<li>name</li>
								<li>password (stored hashed on our servers; never shared with OpenAI)</li>
								<li>a public user ID used for collaboration</li>
								<li>an optional profile photo, if you upload one</li>
							</ul>
							<p>
								Apple Sign In and Google Sign In send us an identity token and the profile
								information you allow (typically email and name). We do not offer Facebook or
								Twitter login.
							</p>

							<h3>Voice input (optional)</h3>
							<p>
								If you tap the microphone in Wishbox Assistant, we record only while the mic is
								active. That audio is sent to our servers and then to OpenAI to convert it to text.
								We do not store the recording on Wishbox servers. The transcript is treated as a
								message you sent (including for chat history). Language is detected automatically,
								so you can speak Greek or another language even if your phone is set to English. We
								do not use voice recordings to identify you or to build a voiceprint.
							</p>

							<h3>Purchases</h3>
							<p>
								Wishbox+ is billed by Apple or Google. We do not collect payment card numbers. We
								store subscription receipts, product identifiers, transaction identifiers, and
								expiration needed to unlock Plus. Payment data is processed by Apple and Google
								under their notices:{" "}
								<a href="https://www.apple.com/legal/privacy/">Apple Privacy Policy</a> and{" "}
								<a href="https://policies.google.com/privacy">Google Privacy Policy</a>.
							</p>

							<h3>Device identifiers</h3>
							<p>
								We use a device identifier to bind Wishbox+ entitlements to your installation and
								may store a push token (FCM or APNs) if you enable notifications. These identifiers
								are not used for advertising and we do not collect IDFA or Google Advertising ID.
							</p>

							<h3>Push notifications</h3>
							<p>
								If you allow notifications, we store a device push token and your notification
								preferences so we can send collaboration or account updates. You can disable
								notifications in your device settings.
							</p>

							<h3>Information we do not collect</h3>
							<ul>
								<li>precise or coarse location</li>
								<li>
									contacts, calendar, clipboard, or camera (beyond an optional photo-library avatar)
								</li>
								<li>advertising identifiers, analytics SDKs, or crash-reporting SDKs</li>
								<li>offer walls or in-app advertising</li>
							</ul>

							<h2 id="collaboration" style={headingStyle}>
								2. COLLABORATION
							</h2>
							<p>
								If you share a wishbox with others, we store on our servers the shared item metadata
								(titles, icons, currency, balances as needed to sync) and the transactions belonging
								to that shared wishbox, including amounts, dates, categories, and descriptions.
								Members of that collaboration can see the shared data. Unrelated wishboxes and
								accounts stay on your device.
							</p>

							<h2 id="cloud-sync" style={headingStyle}>
								3. CLOUD SYNC
							</h2>
							<p>
								Cloud Sync is optional and requires Wishbox+. If you connect iCloud or Google Drive,
								the app uploads a copy of the database on your device (transactions, wishboxes,
								categories, settings, and related app data) to that service under your account.
								Wishbox does not host that file on our servers. The copy is stored and processed by
								the service you choose under their terms and privacy notices:
							</p>
							<ul>
								<li>
									<a href="https://www.apple.com/legal/privacy/">Apple Privacy Policy</a> (iCloud)
								</li>
								<li>
									<a href="https://policies.google.com/privacy">Google Privacy Policy</a> (Google
									Drive)
								</li>
							</ul>
							<p>
								The upload is a zip of the app database. Wishbox does not encrypt that file with a
								separate password or key. Access is controlled by your iCloud or Google Drive
								account and that provider's security. Anyone with access to that account can read
								the copy.
							</p>
							<p>
								When more than one device syncs, the newest copy wins. Restoring a cloud copy
								replaces the data on that device.
							</p>
							<p>
								You can disconnect Cloud Sync in the app. Disconnecting stops new uploads from this
								device; it does not by itself delete the file already stored in your iCloud or
								Google Drive account. To remove that copy, delete the Wishbox Sync files in that
								account yourself. We cannot delete them for you. Cloud Sync is separate from
								Collaboration.
							</p>

							<h2 id="assistant" style={headingStyle}>
								4. AI ASSISTANT AND OPENAI
							</h2>
							<p>
								Wishbox Assistant is optional. It requires a Wishbox+ subscription and your explicit
								agreement in the app. You can revoke that agreement in Settings → AI Assistant.
								Turning consent off stops new data from being sent to OpenAI. It does not by itself
								delete stored chat history or collaboration data you already published; you can
								delete conversations in the assistant history, or email us to request deletion.
							</p>
							<p>
								When you use the assistant, we send to OpenAI (via our API) the data needed to
								answer you, which may include:
							</p>
							<ul>
								<li>your messages and the assistant replies</li>
								<li>
									voice recordings you send with the microphone, used only to transcribe them into
									text (we do not keep the audio file)
								</li>
								<li>filters such as wishbox or source titles and date ranges</li>
								<li>aggregated statistics you allow for a source</li>
								<li>
									published collaboration transactions when you choose to share a source (recent
									amounts, dates, descriptions, and categories)
								</li>
								<li>
									import samples (file headers and a small number of sample rows) if you use import
									help
								</li>
							</ul>
							<p>
								Voice is recorded only while the microphone is active. We send the audio to OpenAI
								for speech-to-text with automatic language detection, then handle the transcript
								like a typed message. We do not store voice recordings on Wishbox servers, and we do
								not use them to create a voiceprint.
							</p>
							<p>
								We store a consent receipt on our servers (account, terms version, platform, app
								version, IP address, user agent, and time). We store assistant chat history on our
								servers so you can reopen conversations, up to the limit you set in the app (default
								50, maximum 100). Older conversations beyond that limit are removed automatically.
							</p>
							<p>
								OpenAI processes this data to transcribe optional voice input and generate replies.
								We do not use assistant data for advertising. We configure our OpenAI chat requests
								so they are not stored in OpenAI's dashboard. Voice is sent through OpenAI's audio
								transcription API; we do not keep the recording. OpenAI's handling of API data,
								including whether it is used to train models, is described in{" "}
								<a href="https://openai.com/policies/privacy-policy">OpenAI's Privacy Policy</a> and{" "}
								<a href="https://openai.com/enterprise-privacy/">
									OpenAI's enterprise privacy information
								</a>
								. We do not control OpenAI's policies and they may change.
							</p>
							<p>
								Assistant output can be wrong. It is not financial, tax, or investment advice. Do
								not paste other people's private data into the assistant unless you have the right
								to do so. Do not record other people with the microphone unless you have the right
								to do so.
							</p>

							<h2 id="infouse" style={headingStyle}>
								5. HOW DO WE PROCESS YOUR INFORMATION?
							</h2>
							<p>
								<em>
									<strong>In Short:</strong> We process information to provide the features you use,
									authenticate you, validate subscriptions, sync shared wishboxes, run Cloud Sync to
									your own storage, run the assistant you opted into, send the notifications you
									enabled, secure the Services, and comply with law.
								</em>
							</p>
							<ul>
								<li>
									<strong>Account and authentication.</strong> Create and maintain your optional
									account, including Apple or Google sign-in.
								</li>
								<li>
									<strong>App functionality.</strong> Collaboration sync, Cloud Sync to a service
									you connect, Wishbox+ entitlement checks, currency rates, avatars, and support.
								</li>
								<li>
									<strong>AI Assistant.</strong> Only after consent, to generate replies through
									OpenAI, including transcribing optional voice input.
								</li>
								<li>
									<strong>Security and operations.</strong> We keep limited API logs (method, route,
									status, and technical metadata). Assistant message contents, financial filters,
									tool payloads, and voice audio are redacted from those logs.
								</li>
								<li>
									<strong>Legal.</strong> To comply with law, enforce our terms, or respond to
									lawful requests.
								</li>
							</ul>
							<p>
								We do not sell personal information and we do not use it for third-party or
								developer advertising.
							</p>

							<h2 id="legalbases" style={headingStyle}>
								6. WHAT LEGAL BASES DO WE RELY ON?
							</h2>
							<p>
								<em>
									<strong>In Short:</strong> We process personal information when we have a valid
									reason under applicable law.
								</em>
							</p>
							<p>
								<strong>If you are in the EU or UK:</strong> we may rely on (1) performance of a
								contract (providing the app features you request, including Cloud Sync when you
								connect a service), (2) consent (AI Assistant and optional notifications), (3)
								legitimate interests (securing the API, preventing abuse), and (4) legal
								obligations. Connecting Cloud Sync is a feature you turn on; we do not store a
								separate Cloud Sync consent receipt.
							</p>
							<p>
								<strong>If you are in Canada:</strong> we may process information with your express
								or implied consent, or as otherwise permitted by law. You may withdraw consent by
								contacting us or, for the assistant, in Settings → AI Assistant. You can disconnect
								Cloud Sync in the app at any time.
							</p>

							<h2 id="whoshare" style={headingStyle}>
								7. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
							</h2>
							<p>
								<em>
									<strong>In Short:</strong> We share information with the processors needed to run
									a feature you use, and as required by law.
								</em>
							</p>
							<ul>
								<li>
									<strong>Apple.</strong> Sign In, App Store billing, push (APNs), and iCloud if you
									turn on Cloud Sync with iCloud.
								</li>
								<li>
									<strong>Google.</strong> Sign In, Google Play billing, Firebase Cloud Messaging
									for push, and Google Drive if you turn on Cloud Sync with Google Drive. We do not
									use Firebase Analytics.
								</li>
								<li>
									<strong>OpenAI.</strong> Wishbox Assistant messages, optional voice recordings for
									transcription, and the finance context described above, only after you agree.
								</li>
								<li>
									<strong>Collaboration members.</strong> Shared wishbox data you publish, and your
									name, email, and avatar as shown in the member list.
								</li>
								<li>
									<strong>Business transfers.</strong> If we merge, sell assets, or similar,
									information may transfer subject to this notice.
								</li>
							</ul>
							<p>
								We do not run offer walls, ads networks, or social logins other than Apple and
								Google.
							</p>

							<h2 id="sociallogins" style={headingStyle}>
								8. HOW DO WE HANDLE SOCIAL LOGINS?
							</h2>
							<p>
								If you sign in with Apple or Google, we receive the profile information those
								providers share with us (typically name and email) and use it only to create or link
								your Wishbox account. Review Apple's and Google's privacy notices for their
								practices. We do not control those providers.
							</p>

							<h2 id="retention" style={headingStyle}>
								9. HOW LONG DO WE KEEP YOUR INFORMATION?
							</h2>
							<p>
								<em>
									<strong>In Short:</strong> We keep information as long as needed for the purposes
									in this notice, unless a longer period is required by law.
								</em>
							</p>
							<ul>
								<li>Account data lasts while your account is open.</li>
								<li>
									Assistant history is kept up to the number of conversations you choose in the app,
									then older entries are deleted. Voice recordings are not stored; only the
									transcript may remain in chat history.
								</li>
								<li>
									Consent receipts are kept until you revoke consent or we delete the account.
								</li>
								<li>
									Collaboration data lasts while the shared wishbox exists or until it is removed.
								</li>
								<li>
									Cloud Sync files remain in your iCloud or Google Drive account until you delete
									them there. We do not host those files and cannot delete them for you.
								</li>
							</ul>
							<p>
								When we no longer need personal information, we delete or anonymize it, or isolate
								it in backups until deletion is possible.
							</p>

							<h2 id="infosafe" style={headingStyle}>
								10. HOW DO WE KEEP YOUR INFORMATION SAFE?
							</h2>
							<p>
								We use HTTPS for data in transit to our servers and access controls on those
								servers. Cloud Sync files in your iCloud or Google Drive account are protected by
								that provider and your account, not by a Wishbox encryption key. No transmission or
								storage system is 100% secure. Use the Services on a device you control and keep
								your account credentials private.
							</p>

							<h2 id="transfers" style={headingStyle}>
								11. INTERNATIONAL TRANSFERS
							</h2>
							<p>
								We are based in Greece. Our servers and processors (including Apple, Google, and
								OpenAI) may process information in the United States or other countries. Those
								countries may have different data-protection laws than your own. Google Drive Cloud
								Sync transfers go from your device to your Google account.
							</p>

							<h2 id="privacyrights" style={headingStyle}>
								12. WHAT ARE YOUR PRIVACY RIGHTS?
							</h2>
							<p>
								<em>
									<strong>In Short:</strong> In some regions, including the EEA, UK, and Canada, you
									can access, correct, or delete personal information we hold, or withdraw consent.
								</em>
							</p>
							<p>
								Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to request access,
								correction, or deletion of your account, collaboration data, or assistant history.
								We will respond in line with applicable law. You can also:
							</p>
							<ul>
								<li>update profile information in the app</li>
								<li>revoke AI Assistant consent in Settings → AI Assistant</li>
								<li>delete individual assistant conversations in the app</li>
								<li>disconnect Cloud Sync in the app</li>
								<li>delete Wishbox Sync files in your iCloud or Google Drive account</li>
								<li>disable notifications in device settings</li>
							</ul>
							<p>
								If you are in the EEA or UK and believe we process your information unlawfully, you
								may complain to your local supervisory authority:{" "}
								<a href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm">
									EU authorities
								</a>
								. Switzerland: <a href="https://www.edoeb.admin.ch/edoeb/en/home.html">FDPIC</a>.
							</p>
							<p>
								<strong>Withdrawing consent:</strong> Withdrawal does not affect processing that
								already happened. Revoking AI consent does not automatically erase prior chat
								history or published collaboration data.
							</p>
							<p>
								Upon a request to terminate your account, we will delete or deactivate the account
								and information from our active databases, except where we must retain limited
								records for fraud prevention, legal claims, or legal requirements.
							</p>

							<h2 style={headingStyle}>13. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
							<p>
								We do not currently respond to DNT browser signals. We do not use third-party
								advertising or analytics SDKs in the app or on this website.
							</p>

							<h2 style={headingStyle}>
								14. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
							</h2>
							<p>
								<em>
									<strong>In Short:</strong> Yes. California residents have rights to know, delete,
									and not be discriminated against for exercising privacy rights.
								</em>
							</p>
							<p>
								California Civil Code Section 1798.83 (Shine the Light) allows an annual request
								about personal information disclosed to third parties for direct marketing. We do
								not disclose personal information for direct marketing.
							</p>
							<p>
								<strong>What categories of personal information do we collect?</strong>
							</p>
							<table style={tableStyle}>
								<thead>
									<tr>
										<th style={cellStyle}>Category</th>
										<th style={cellStyle}>Examples</th>
										<th style={cellStyle}>Collected</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td style={cellStyle}>A. Identifiers</td>
										<td style={cellStyle}>
											Email, account name, user ID, device ID, IP address associated with API or
											consent records
										</td>
										<td style={cellStyle}>YES</td>
									</tr>
									<tr>
										<td style={cellStyle}>B. Customer Records</td>
										<td style={cellStyle}>
											Name, contact information, financial information you enter
										</td>
										<td style={cellStyle}>YES</td>
									</tr>
									<tr>
										<td style={cellStyle}>C. Protected classifications</td>
										<td style={cellStyle}>Gender, date of birth</td>
										<td style={cellStyle}>NO</td>
									</tr>
									<tr>
										<td style={cellStyle}>D. Commercial information</td>
										<td style={cellStyle}>Wishbox+ purchase history and entitlements</td>
										<td style={cellStyle}>YES</td>
									</tr>
									<tr>
										<td style={cellStyle}>E. Biometric information</td>
										<td style={cellStyle}>Fingerprints, voiceprints</td>
										<td style={cellStyle}>NO</td>
									</tr>
									<tr>
										<td style={cellStyle}>F. Internet or network activity</td>
										<td style={cellStyle}>
											Limited API operational logs; assistant chats are stored as user content, not
											for advertising
										</td>
										<td style={cellStyle}>YES</td>
									</tr>
									<tr>
										<td style={cellStyle}>G. Geolocation data</td>
										<td style={cellStyle}>Device location</td>
										<td style={cellStyle}>NO</td>
									</tr>
									<tr>
										<td style={cellStyle}>H. Audio, electronic, visual</td>
										<td style={cellStyle}>
											Optional profile photo; optional voice recordings processed for assistant
											speech-to-text (not retained)
										</td>
										<td style={cellStyle}>YES</td>
									</tr>
									<tr>
										<td style={cellStyle}>I. Professional information</td>
										<td style={cellStyle}>Employment history</td>
										<td style={cellStyle}>NO</td>
									</tr>
									<tr>
										<td style={cellStyle}>J. Education information</td>
										<td style={cellStyle}>Student records</td>
										<td style={cellStyle}>NO</td>
									</tr>
									<tr>
										<td style={cellStyle}>K. Inferences</td>
										<td style={cellStyle}>Profiles built for advertising</td>
										<td style={cellStyle}>NO</td>
									</tr>
								</tbody>
							</table>
							<p>
								Kostas Filios has not sold personal information and will not sell personal
								information of app users, website visitors, or other consumers. Service providers
								(Apple, Google, OpenAI) process information on our behalf for the features above.
							</p>
							<p>
								To exercise CCPA rights, email{" "}
								<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will verify your
								identity before fulfilling a request. We will not discriminate against you for
								exercising your rights.
							</p>

							<h2 style={headingStyle}>15. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
							<p>
								Yes. The updated version is shown by the "Last updated" date and takes effect when
								posted. Material changes to the AI Assistant are also tied to a consent version in
								the app; you may be asked to agree again before using the assistant.
							</p>

							<h2 id="contact" style={headingStyle}>
								16. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
							</h2>
							<p>
								Kostas Filios, Athens, Greece. Email:{" "}
								<strong>
									<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
								</strong>
								.
							</p>
							<p>
								Related documents: <Link href="/terms">Terms of Service</Link> and{" "}
								<Link href="/eula">End User License Agreement</Link>.
							</p>

							<h2 style={headingStyle}>
								17. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
							</h2>
							<p>
								Email{" "}
								<strong>
									<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
								</strong>{" "}
								to request access, correction, or deletion of your account and related server data
								(including assistant history and collaboration data we host). You can also manage
								assistant consent and conversation history in the app. Cloud Sync copies live in
								your own cloud account; delete those files there, or disconnect Cloud Sync in the
								app to stop new uploads.
							</p>
						</FlexColumn>
					</SectionSmall>
				</Article>
			</Section>
		</Layout>
	);
}
